import React, {
  ComponentType,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import AlignVerticalBottomOutlinedIcon from '@mui/icons-material/AlignVerticalBottomOutlined';
import AlignVerticalCenterOutlinedIcon from '@mui/icons-material/AlignVerticalCenterOutlined';
import AlignVerticalTopOutlinedIcon from '@mui/icons-material/AlignVerticalTopOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import clsx from 'clsx';
import InputAdornment from '@mui/material/InputAdornment';
import AutoCompletedInput from 'core/components/AutoComplete';
import ChevronDownIcon from 'core/icons/chevronDown';
import SearchIcon from 'core/icons/search';
import ToolTip from 'core/components/Tooltip';
import {
  AutoFillAliasDTO,
  FieldTypeDTO,
  FormCategoryRuleDTO,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import Select from 'core/components/Select';
import Button from 'pages/Dashboard/components/Button';
import Input from 'pages/Dashboard/components/Input';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import {
  getAssigneeSwatchClassName,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldAssigneeStyles';
import DocumentPanel from 'views/EMR/Settings/PdfTemplateBuilder/components/DocumentPanel';
import {
  PdfTemplateDocument,
  PdfTemplateFieldDefinition,
  PdfTemplateField,
  PdfTemplateFieldType,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  getFieldTypeDefinitions,
  canSetAutoFillAlias,
  canToggleFieldOptional,
  fieldSupportsAlignment,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldTypes';
import {
  DEFAULT_HORIZONTAL_ALIGNMENT,
  DEFAULT_VERTICAL_ALIGNMENT,
  getRadioGroupIsRequired,
  getRadioGroupAssignee,
  getCheckboxGroupIsRequired,
  getCheckboxGroupAssignee,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/formFields';
import {
  getFieldTypeIcon,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldTypeIcons';
import isNil from 'lodash/isNil';
import {
  AliasOption,
  buildAliasOptions,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldAlias';

type AlignmentIconProps = {
  className?: string;
};

type AlignmentOption = {
  icon: ComponentType<AlignmentIconProps>;
  tooltip: string;
  value: string;
};

type FontSizeOption = {
  name: string;
  value: number;
};

function getSharedValue<T>(
  selectedFields: PdfTemplateField[],
  getValue: (field: PdfTemplateField) => T | null | undefined,
): T | null {
  if (selectedFields.length === 0) {
    return null;
  }

  const values = selectedFields.map(getValue);
  const first = values[0];

  return values.every((value) => value === first) ? (first ?? null) : null;
}

function resolveSelectedOption<TOption extends { value: TValue }, TValue>({
  options,
  value,
  fallback,
}: {
  options: TOption[];
  value: TValue | null | undefined;
  fallback?: (value: TValue) => TOption | null;
}): TOption | null {
  const selectedOption = options.find((option) => option.value === value);

  if (selectedOption) {
    return selectedOption;
  }

  if (value == null || isNil(fallback)) {
    return null;
  }

  return fallback(value);
}

const HORIZONTAL_ALIGNMENT_OPTIONS: AlignmentOption[] = [
  {
    icon: FormatAlignLeftIcon,
    tooltip: 'Align text to the left',
    value: 'left',
  },
  {
    icon: FormatAlignCenterIcon,
    tooltip: 'Center text horizontally',
    value: 'center',
  },
  {
    icon: FormatAlignRightIcon,
    tooltip: 'Align text to the right',
    value: 'right',
  },
];

const VERTICAL_ALIGNMENT_OPTIONS: AlignmentOption[] = [
  {
    icon: AlignVerticalTopOutlinedIcon,
    tooltip: 'Align text to the top',
    value: 'top',
  },
  {
    icon: AlignVerticalCenterOutlinedIcon,
    tooltip: 'Center text vertically',
    value: 'middle',
  },
  {
    icon: AlignVerticalBottomOutlinedIcon,
    tooltip: 'Align text to the bottom',
    value: 'bottom',
  },
];

const FONT_SIZE_OPTIONS: FontSizeOption[] = Array.from({ length: 17 }, (_, index) => {
  const value = index + 8;

  return {
    name: String(value),
    value,
  };
});

type AssigneeOption = {
  label: string;
  value: FormFieldAssignee;
};

const ASSIGNEE_OPTIONS: AssigneeOption[] = [
  { label: 'Patient', value: FormFieldAssignee.Patient },
  { label: 'Clinician', value: FormFieldAssignee.Clinician },
];

function renderAssigneeOptionLabel(option: AssigneeOption) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={getAssigneeSwatchClassName(option.value)} aria-hidden />
      {option.label}
    </span>
  );
}

type OptionalFieldProps = {
  id: string;
  isRequired: boolean;
  onChange: (isRequired: boolean) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  infoTooltip?: string;
};

const SIGNATURE_OPTIONAL_DISABLED_TOOLTIP = 'Signature fields are always required and cannot be marked optional.';
const ALIAS_BLOCKED_TOOLTIP = 'Aliases apply to text fields only. Checkboxes and radio groups must be filled by the signer.';
const MULTI_SELECT_DISABLED_TOOLTIP = 'This property cannot be edited when multiple fields are selected.';
const RADIO_GROUP_OPTIONAL_TOOLTIP = 'Sets whether the entire radio group is optional or required.';
const RADIO_GROUP_ASSIGNEE_TOOLTIP = 'Sets the assignee for the entire radio group.';

type MultiSelectDisabledFieldProps = {
  isMultiSelect: boolean;
  children: ReactNode;
};

function MultiSelectDisabledField({
  isMultiSelect,
  children,
}: MultiSelectDisabledFieldProps) {
  return !isMultiSelect ? children : (
    <ToolTip title={MULTI_SELECT_DISABLED_TOOLTIP}>
      <span className="flex w-full min-w-0 flex-col gap-5">
        {children}
      </span>
    </ToolTip>
  );
}

function OptionalField({
  id,
  isRequired,
  onChange,
  disabled = false,
  disabledTooltip,
  infoTooltip,
}: OptionalFieldProps) {
  const optionalLabel = infoTooltip ? (
    <span className="inline-flex items-center gap-1">
      Optional
      <ToolTip title={infoTooltip}>
        <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
      </ToolTip>
    </span>
  ) : 'Optional';

  return (
    <Checkbox
      id={id}
      label={optionalLabel}
      checked={!isRequired}
      disabled={disabled}
      readonly={disabled}
      readonlyMessage={disabled ? disabledTooltip : undefined}
      onChange={(checked) => {
        onChange(!checked);
      }}
    />
  );
}

type FieldAliasControlProps = {
  id: string;
  placeholderClosed: string;
  disabled: boolean;
  showBlockedTooltip: boolean;
  selectedAlias: AliasOption | null | undefined;
  aliasInputValue: string;
  aliasDefinitions: AliasOption[];
  isAliasOpen: boolean;
  onAliasChange: (aliasOption: AliasOption | AliasOption[] | null) => void;
  onAliasOpen: () => void;
  onAliasClose: () => void;
  onAliasFocus: () => void;
  onAliasInputChange: (value: string, reason: string) => void;
};

function FieldAliasControl({
  id,
  placeholderClosed,
  disabled,
  showBlockedTooltip,
  selectedAlias,
  aliasInputValue,
  aliasDefinitions,
  isAliasOpen,
  onAliasChange,
  onAliasOpen,
  onAliasClose,
  onAliasFocus,
  onAliasInputChange,
}: FieldAliasControlProps) {
  const aliasInput = (
    <AutoCompletedInput<AliasOption>
      id={id}
      className="w-full"
      value={selectedAlias}
      inputValue={aliasInputValue}
      options={aliasDefinitions}
      getLabel={(option) => option.label}
      groupBy={(option) => option.groupDisplayName}
      renderGroup={({
        key,
        group,
        children,
      }) => (
        <div key={key}>
          <div className="p-2 text-primary-700 font-semibold text-xs">
            {group}
          </div>
          <ul className="px-1">{children}</ul>
        </div>
      )}
      dropdownClassName="!p-0"
      placeholder={isAliasOpen
        ? (selectedAlias?.label ?? placeholderClosed)
        : placeholderClosed}
      disabled={disabled}
      popupIcon={isAliasOpen
        ? <SearchIcon className="search-icon" />
        : <ChevronDownIcon className="text-gray-500" />}
      clearIcon={selectedAlias
        ? <CloseIcon className="!h-4 !w-4 -m-0.5" />
        : null}
      onChange={onAliasChange}
      onOpen={onAliasOpen}
      onClose={onAliasClose}
      onFocus={onAliasFocus}
      onInputChange={(_, value, reason) => {
        onAliasInputChange(value, reason);
      }}
      openOnFocus
      selectOnFocus
      clearOnBlur={false}
      isOptionEqualToValue={(option, value) => (
        option.value === (value as AliasOption)?.value
      )}
    />
  );

  return showBlockedTooltip ? (
    <ToolTip title={ALIAS_BLOCKED_TOOLTIP}>
      <span className="block w-full min-w-0">
        {aliasInput}
      </span>
    </ToolTip>
  ) : aliasInput;
}

type SignerFieldProps = {
  selectId: string;
  selectedAssigneeOption: AssigneeOption | undefined;
  allowsAssigneeOverride: boolean;
  categoryRule?: FormCategoryRuleDTO | null;
  onFieldAssigneeChange: (assignee: FormFieldAssignee) => void;
  infoTooltip?: string;
  disabled?: boolean;
  disabledTooltip?: string;
};

function SignerField({
  selectId,
  selectedAssigneeOption,
  allowsAssigneeOverride,
  categoryRule,
  onFieldAssigneeChange,
  infoTooltip,
  disabled = false,
  disabledTooltip,
}: SignerFieldProps) {
  const filteredAssigneeOptions = ASSIGNEE_OPTIONS.filter(
    (option) => categoryRule?.allowedAssignees?.includes(option.value) ?? true,
  );

  const signerControl = !allowsAssigneeOverride ? (
    <span className="inline-flex items-center gap-2 text-sm text-gray-700">
      {selectedAssigneeOption
        ? renderAssigneeOptionLabel(selectedAssigneeOption)
        : null}
    </span>
  ) : (
    <Select<AssigneeOption>
      id={selectId}
      className="w-full"
      selectClassName="!w-full"
      value={selectedAssigneeOption}
      options={filteredAssigneeOptions}
      disabled={disabled}
      getValue={(option) => option?.value ?? ''}
      getLabel={(option) => renderAssigneeOptionLabel(option)}
      renderValue={(value) => {
        const option = filteredAssigneeOptions.find(
          (item) => String(item.value) === String(value),
        );

        return option ? renderAssigneeOptionLabel(option) : value;
      }}
      onChange={(option) => {
        onFieldAssigneeChange((option as AssigneeOption).value);
      }}
    />
  );

  const signerFieldContent = (
    <div className="flex flex-col gap-2">
      <div className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
        Assignee
        {infoTooltip ? (
          <ToolTip title={infoTooltip}>
            <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
          </ToolTip>
        ) : null}
      </div>
      {signerControl}
    </div>
  );

  return disabled && disabledTooltip ? (
    <ToolTip title={disabledTooltip}>
      <span className="block w-full min-w-0">
        {signerFieldContent}
      </span>
    </ToolTip>
  ) : signerFieldContent;
}

type Props = {
  selectedField: PdfTemplateField | null;
  selectedFieldCount?: number;
  selectedFieldIds?: string[];
  fields: PdfTemplateField[];
  document: PdfTemplateDocument;
  aliasOptions?: AutoFillAliasDTO[] | null;
  fieldTypes?: FieldTypeDTO[] | null;
  categoryRule?: FormCategoryRuleDTO | null;
  isMixedAssigneeMode?: boolean;
  defaultAssigneeForNewField?: FormFieldAssignee;
  isReplacingDocument?: boolean;
  canReplaceDocument?: boolean;
  isAwaitingRadioGroupOption?: boolean;
  isAwaitingCheckboxGroupOption?: boolean;
  fieldHistoryLabel?: string | null;
  canUndoFieldChange?: boolean;
  canRedoFieldChange?: boolean;
  onFieldAliasChange: (aliasId: string | null) => void;
  onFieldVerticalAlignmentChange: (alignment: string | null) => void;
  onFieldHorizontalAlignmentChange: (alignment: string | null) => void;
  onFieldFontSizeChange: (fontSize: number | null) => void;
  onFieldAssigneeChange: (assignee: FormFieldAssignee) => void;
  onFieldRequiredChange: (isRequired: boolean) => void;
  onFieldNameChange: (fieldName: string) => void;
  onRadioGroupNameChange: (groupName: string) => void;
  onCheckboxGroupNameChange: (groupName: string) => void;
  onFieldTypeChange: (fieldType: PdfTemplateFieldType) => void;
  onAddRadioGroupOption: () => void;
  onAddCheckboxGroupOption: () => void;
  onSelectField: (fieldId: string | null) => void;
  onClearSelection: () => void;
  onUndoFieldChange?: () => void;
  onRedoFieldChange?: () => void;
  onReplaceDocument: (file: File) => Promise<void>;
};

export default function PropertiesPanel({
  selectedField,
  selectedFieldCount = 0,
  selectedFieldIds = [],
  fields,
  document,
  aliasOptions,
  fieldTypes,
  categoryRule,
  isMixedAssigneeMode = false,
  defaultAssigneeForNewField = FormFieldAssignee.Patient,
  isReplacingDocument,
  canReplaceDocument,
  isAwaitingRadioGroupOption,
  isAwaitingCheckboxGroupOption,
  fieldHistoryLabel,
  canUndoFieldChange = false,
  canRedoFieldChange = false,
  onFieldAliasChange,
  onFieldVerticalAlignmentChange,
  onFieldHorizontalAlignmentChange,
  onFieldFontSizeChange,
  onFieldAssigneeChange,
  onFieldRequiredChange,
  onFieldNameChange,
  onRadioGroupNameChange,
  onCheckboxGroupNameChange,
  onFieldTypeChange,
  onAddRadioGroupOption,
  onAddCheckboxGroupOption,
  onSelectField,
  onClearSelection,
  onUndoFieldChange,
  onRedoFieldChange,
  onReplaceDocument,
}: Props) {
  const [fieldTypeInputValue, setFieldTypeInputValue] = useState('');
  const [isFieldTypeOpen, setIsFieldTypeOpen] = useState(false);
  const [aliasInputValue, setAliasInputValue] = useState('');
  const [isAliasOpen, setIsAliasOpen] = useState(false);
  const [fontSizeInputValue, setFontSizeInputValue] = useState('');
  const [isFontSizeOpen, setIsFontSizeOpen] = useState(false);
  const fieldTypeDefinitions = getFieldTypeDefinitions(fieldTypes);
  const allowsAssigneeOverride = (categoryRule?.allowedAssignees?.length ?? 0) > 1
    && isMixedAssigneeMode;
  const selectedFieldType = fieldTypeDefinitions.find(
    (fieldTypeDefinition) => fieldTypeDefinition.type === selectedField?.type,
  ) ?? null;
  const aliasDefinitions = buildAliasOptions(aliasOptions);

  const selectedAlias = resolveSelectedOption({
    options: aliasDefinitions,
    value: selectedField?.aliasId,
  });

  const isMultiSelect = selectedFieldCount > 1;
  const isNonBatchPropertyDisabled = isMultiSelect;
  const selectedFields = fields.filter((field) => selectedFieldIds.includes(field.id));
  let formatTargetFields = selectedFields;
  if (!isMultiSelect) {
    formatTargetFields = selectedField ? [selectedField] : [];
  }

  const sharedFontSize = getSharedValue(
    formatTargetFields,
    (field) => field.fontSize ?? null,
  );
  const sharedHorizontalAlignment = getSharedValue(
    formatTargetFields,
    (field) => field.horizontalAlignment ?? DEFAULT_HORIZONTAL_ALIGNMENT,
  );
  const sharedVerticalAlignment = getSharedValue(
    formatTargetFields,
    (field) => field.verticalAlignment ?? DEFAULT_VERTICAL_ALIGNMENT,
  );

  const selectedHorizontalAlignment = resolveSelectedOption({
    options: HORIZONTAL_ALIGNMENT_OPTIONS,
    value: sharedHorizontalAlignment,
  });

  const selectedVerticalAlignment = resolveSelectedOption({
    options: VERTICAL_ALIGNMENT_OPTIONS,
    value: sharedVerticalAlignment,
  });

  const selectedFontSize = resolveSelectedOption({
    options: FONT_SIZE_OPTIONS,
    value: sharedFontSize,
    fallback: (fontSize) => (
      Number.isFinite(fontSize)
        ? {
          name: String(fontSize),
          value: fontSize,
        }
        : null
    ),
  });

  useEffect(() => {
    setIsFieldTypeOpen(false);
    setFieldTypeInputValue(selectedFieldType?.label ?? '');
  }, [selectedFieldType?.label, selectedField?.id]);

  useEffect(() => {
    setIsAliasOpen(false);
    setAliasInputValue(selectedAlias?.label ?? '');
  }, [selectedAlias?.label, selectedField?.id]);

  useEffect(() => {
    setIsFontSizeOpen(false);
    setFontSizeInputValue(selectedFontSize?.value?.toString() ?? '');
  }, [selectedFontSize?.value, selectedField?.id, selectedFieldIds]);

  if (isNil(selectedField)) {
    return (
      <DocumentPanel
        document={document}
        isReplacingDocument={isReplacingDocument}
        canReplaceDocument={canReplaceDocument}
        fieldHistoryLabel={fieldHistoryLabel}
        canUndoFieldChange={canUndoFieldChange}
        canRedoFieldChange={canRedoFieldChange}
        onUndoFieldChange={onUndoFieldChange}
        onRedoFieldChange={onRedoFieldChange}
        onReplaceDocument={onReplaceDocument}
      />
    );
  }
  const isOptionGroupField = selectedField?.type === 'radioGroup'
    || selectedField?.type === 'checkboxGroup';
  const isCheckboxGroupField = selectedField?.type === 'checkboxGroup';
  const selectedOptionGroupFields = isOptionGroupField
    ? fields.filter(
      (field) => (field.type === 'radioGroup' || field.type === 'checkboxGroup')
        && (field.groupId ?? field.id) === (selectedField.groupId ?? selectedField.id),
    )
    : [];
  const isAwaitingOptionGroupOption = Boolean(
    isAwaitingRadioGroupOption || isAwaitingCheckboxGroupOption,
  );

  let selectedFieldAssignee = selectedField?.assignee ?? defaultAssigneeForNewField;
  if (isOptionGroupField) {
    selectedFieldAssignee = isCheckboxGroupField
      ? getCheckboxGroupAssignee(selectedOptionGroupFields)
      : getRadioGroupAssignee(selectedOptionGroupFields);
  }

  const getAssigneeForField = (field: PdfTemplateField) => {
    if (field.type === 'radioGroup' || field.type === 'checkboxGroup') {
      const groupId = field.groupId ?? field.id;
      const groupFields = fields.filter(
        (groupField) => (groupField.type === 'radioGroup' || groupField.type === 'checkboxGroup')
          && (groupField.groupId ?? groupField.id) === groupId,
      );

      return field.type === 'checkboxGroup'
        ? getCheckboxGroupAssignee(groupFields)
        : getRadioGroupAssignee(groupFields);
    }

    return field.assignee ?? defaultAssigneeForNewField;
  };

  const displayAssignee = isMultiSelect
    ? getSharedValue(selectedFields, getAssigneeForField)
    : selectedFieldAssignee;

  const selectedAssigneeOption = isMultiSelect
    ? ASSIGNEE_OPTIONS.find((option) => option.value === displayAssignee)
    : ASSIGNEE_OPTIONS.find((option) => option.value === selectedFieldAssignee)
      ?? ASSIGNEE_OPTIONS[0];

  const shouldShowAlignmentOptions = isMultiSelect
    ? formatTargetFields.some((field) => fieldSupportsAlignment(field.type))
    : fieldSupportsAlignment(selectedField.type);
  const canSetAlias = canSetAutoFillAlias(selectedField?.type);

  const SelectedFieldTypeIcon = selectedFieldType
    ? getFieldTypeIcon(selectedFieldType.type)
    : TextFieldsIcon;
  const shouldShowFieldTypeIcon = !isFieldTypeOpen && !!selectedFieldType;
  const fieldTypeLabel = (
    <span className="inline-flex items-center gap-1">
      Field type
      <ToolTip title="Choose the type of field that will be placed on the document.">
        <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
      </ToolTip>
    </span>
  );
  const fieldAliasLabel = (
    <span className="inline-flex items-center gap-1">
      Field alias
      <span className="text-[11px] italic font-normal text-gray-400">optional</span>
      <ToolTip title="Maps this field to system data. This field will be filled automatically.">
        <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
      </ToolTip>
    </span>
  );
  const horizontalAlignmentLabel = (
    <span className="inline-flex items-center gap-1">
      Horizontal alignment
      <ToolTip title="Choose how the field text is aligned from left to right.">
        <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
      </ToolTip>
    </span>
  );
  const verticalAlignmentLabel = (
    <span className="inline-flex items-center gap-1">
      Vertical alignment
      <ToolTip title="Choose how the field text is positioned from top to bottom.">
        <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
      </ToolTip>
    </span>
  );
  const fontSizeLabel = (
    <span className="inline-flex items-center gap-1">
      Font size
      <span className="text-[11px] italic font-normal text-gray-400">optional</span>
      <ToolTip title="Choose the font size used when text is rendered in this field.">
        <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
      </ToolTip>
    </span>
  );
  const handleAliasChange = (
    aliasOption: AliasOption | AliasOption[] | null,
  ) => {
    if (isNil(aliasOption)) {
      setAliasInputValue('');
      onFieldAliasChange(null);
      return;
    }

    if (!Array.isArray(aliasOption)) {
      setAliasInputValue(aliasOption.label);
      setIsAliasOpen(false);
      onFieldAliasChange(aliasOption.value);
    }
  };
  const handleFieldTypeAutocompleteChange = (
    fieldTypeDefinition:
      | PdfTemplateFieldDefinition
      | PdfTemplateFieldDefinition[]
      | null,
  ) => {
    if (isNil(fieldTypeDefinition)) {
      setFieldTypeInputValue('');
      return;
    }

    if (!Array.isArray(fieldTypeDefinition)) {
      onFieldTypeChange(fieldTypeDefinition.type);
      setFieldTypeInputValue(fieldTypeDefinition.label);
      setIsFieldTypeOpen(false);
    }
  };
  const handleHorizontalAlignmentChange = (
    alignmentOption: AlignmentOption | AlignmentOption[] | null,
  ) => {
    const value = alignmentOption && !Array.isArray(alignmentOption)
      ? alignmentOption.value
      : null;

    onFieldHorizontalAlignmentChange(value);
  };
  const handleVerticalAlignmentChange = (
    alignmentOption: AlignmentOption | AlignmentOption[] | null,
  ) => {
    const value = alignmentOption && !Array.isArray(alignmentOption)
      ? alignmentOption.value
      : null;

    onFieldVerticalAlignmentChange(value);
  };
  const handleFontSizeChange = (
    fontSizeOption: FontSizeOption | FontSizeOption[] | null,
  ) => {
    if (isNil(fontSizeOption)) {
      setFontSizeInputValue('');
      onFieldFontSizeChange(null);
      return;
    }

    if (!Array.isArray(fontSizeOption)) {
      setFontSizeInputValue(fontSizeOption.name);
      setIsFontSizeOpen(false);
      onFieldFontSizeChange(fontSizeOption.value);
    }
  };
  const fieldNameLabel = (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
      Field name
      <ToolTip title="This is the label shown to users for the field.">
        <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
      </ToolTip>
    </span>
  );
  const fieldTypePlaceholderContent = selectedFieldType ? (
    <div className="flex items-center gap-2 text-gray-400">
      <SelectedFieldTypeIcon className="!h-4 !w-4 text-gray-300" />
      <span className="text-sm">
        {selectedFieldType.label}
      </span>
    </div>
  ) : null;

  const renderAlignmentSelector = (
    id: string,
    options: AlignmentOption[],
    selectedOption: AlignmentOption | null,
    onChange: (option: AlignmentOption | null) => void,
  ) => (
    <div className="flex flex-col gap-2">
      <div className="inline-flex items-center gap-2">
        <div className="inline-flex rounded-md bg-gray-100 p-1">
          {options.map((option) => {
            const isSelected = selectedOption?.value === option.value;
            const Icon = option.icon;

            return (
              <ToolTip key={option.value} title={option.tooltip}>
                <button
                  id={`${id}-${option.value}`}
                  type="button"
                  aria-label={option.value}
                  aria-pressed={isSelected}
                  className={clsx(
                    'flex h-8 w-9 items-center justify-center rounded-md text-gray-700 transition-colors duration-150',
                    {
                      'bg-white !text-blue-500 shadow-sm': isSelected,
                      'hover:bg-white/70': !isSelected,
                    },
                  )}
                  onClick={() => {
                    onChange(isSelected ? null : option);
                  }}
                >
                  <Icon className="!h-4.5 !w-4.5" />
                </button>
              </ToolTip>
            );
          })}
        </div>
      </div>
    </div>
  );

  const textFormatOptions = (
    <>
      <div className="flex flex-col gap-2">
        <div className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
          {fontSizeLabel}
        </div>
        <AutoCompletedInput<FontSizeOption>
          id="pdf-field-font-size"
          value={selectedFontSize}
          inputValue={fontSizeInputValue}
          options={FONT_SIZE_OPTIONS}
          getLabel={(option) => option.name}
          placeholder={isFontSizeOpen
            ? (selectedFontSize?.name ?? 'Select font size')
            : 'Select font size'}
          disabled={formatTargetFields.length === 0 || FONT_SIZE_OPTIONS.length === 0}
          popupIcon={isFontSizeOpen
            ? <SearchIcon className="search-icon" />
            : <ChevronDownIcon className="text-gray-500" />}
          clearIcon={selectedFontSize
            ? <CloseIcon className="!h-4 !w-4 -m-0.5" />
            : null}
          onChange={handleFontSizeChange}
          onOpen={() => {
            setIsFontSizeOpen(true);
            setFontSizeInputValue('');
          }}
          onClose={() => {
            setIsFontSizeOpen(false);
            setFontSizeInputValue(selectedFontSize?.name ?? '');
          }}
          onFocus={() => {
            setIsFontSizeOpen(true);
            setFontSizeInputValue('');
          }}
          onInputChange={(_, value, reason) => {
            if (!isFontSizeOpen || reason !== 'reset') {
              setFontSizeInputValue(value);
            }
          }}
          openOnFocus
          selectOnFocus
          clearOnBlur={false}
          isOptionEqualToValue={(option, value) => (
            option.value === (value as FontSizeOption)?.value
          )}
        />
      </div>
      {shouldShowAlignmentOptions ? (
        <>
          <div className="flex flex-col gap-2">
            <div className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
              {horizontalAlignmentLabel}
            </div>
            {renderAlignmentSelector(
              'pdf-field-horizontal-alignment',
              HORIZONTAL_ALIGNMENT_OPTIONS,
              selectedHorizontalAlignment,
              (option) => { handleHorizontalAlignmentChange(option); },
            )}
          </div>
          <div className="flex flex-col gap-2">
            <div className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
              {verticalAlignmentLabel}
            </div>
            {renderAlignmentSelector(
              'pdf-field-vertical-alignment',
              VERTICAL_ALIGNMENT_OPTIONS,
              selectedVerticalAlignment,
              (option) => { handleVerticalAlignmentChange(option); },
            )}
          </div>
        </>
      ) : null}
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-[52px] items-center justify-between border-b border-gray-200 px-4">
        <span className="rounded-full bg-secondary px-4 py-1 text-xs font-semibold text-white">
          {selectedFieldCount > 0
            ? `${selectedFieldCount} field${selectedFieldCount === 1 ? '' : 's'} selected`
            : 'No field selected'}
        </span>
        <button
          type="button"
          className="text-xs font-semibold text-gray-600 transition-colors duration-150 hover:text-secondary"
          onClick={onClearSelection}
        >
          Clear selection
        </button>
      </div>
      <div className="border-b border-gray-200 px-4">
        <div
          className={clsx(
            'flex h-12 items-center gap-2 border-b-2 border-primary',
            'text-sm font-semibold',
          )}
        >
          <TextFieldsIcon className="!h-4 !w-4 text-gray-700" />
          Options
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4 text-sm">
        {isAwaitingOptionGroupOption ? (
          <div className="rounded border border-secondary/15 bg-secondary/10 px-4 py-3 text-center text-sm font-semibold text-secondary">
            Draw a field to connect!
          </div>
        ) : null}
        <MultiSelectDisabledField isMultiSelect={isNonBatchPropertyDisabled}>
        <div className="flex flex-col gap-2">
          <div className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
            {fieldTypeLabel}
          </div>
          <AutoCompletedInput<PdfTemplateFieldDefinition>
            id="pdf-field-type"
            value={selectedFieldType ?? null}
            inputValue={fieldTypeInputValue}
            options={fieldTypeDefinitions}
            getLabel={(option) => option.label}
            disabled={!selectedField || isNonBatchPropertyDisabled}
            popupIcon={isFieldTypeOpen
              ? <SearchIcon className="search-icon" />
              : <ChevronDownIcon className="text-gray-500" />}
            onChange={handleFieldTypeAutocompleteChange}
            onOpen={() => {
              setIsFieldTypeOpen(true);
              setFieldTypeInputValue('');
            }}
            onClose={() => {
              setIsFieldTypeOpen(false);
              setFieldTypeInputValue(selectedFieldType?.label ?? '');
            }}
            onFocus={() => {
              setIsFieldTypeOpen(true);
              setFieldTypeInputValue('');
            }}
            onInputChange={(_, value, reason) => {
              if (isFieldTypeOpen && reason === 'reset') {
                return;
              }
              setFieldTypeInputValue(value);
            }}
            placeholder={isFieldTypeOpen
              ? (fieldTypePlaceholderContent ?? 'Select field type')
              : 'Select field type'}
            classes={{ placeholder: '!left-2' }}
            openOnFocus
            selectOnFocus
            clearOnBlur={false}
            filterSelectedOptions={false}
            isOptionEqualToValue={(option, value) => (
              option.type === (value as PdfTemplateFieldDefinition)?.type
            )}
            groupBy={(option) => (option.grouping ?? 'Ungrouped')}
            InputProps={{
              startAdornment: shouldShowFieldTypeIcon ? (
                <InputAdornment position="start" className="!mr-2">
                  <SelectedFieldTypeIcon className="!h-4 !w-4 text-gray-500" />
                </InputAdornment>
              ) : null,
            }}
            renderGroup={(params) => (
              <li key={params.key}>
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-800">
                  {params.group}
                </div>
                <ul className="p-0">
                  {params.children}
                </ul>
              </li>
            )}
            renderOption={(props, option) => {
              const Icon = getFieldTypeIcon(option.type);

              return (
                <li
                  {...props}
                  key={option.type}
                  className={clsx(
                    props.className,
                    'flex items-center gap-3 px-3 py-2 text-xs',
                  )}
                >
                  <Icon className="!h-4 !w-4 shrink-0 text-gray-500" />
                  <span className="font-medium text-gray-800">
                    {option.label}
                  </span>
                </li>
              );
            }}
          />
        </div>
        </MultiSelectDisabledField>
        {isOptionGroupField ? (
          <>
            <MultiSelectDisabledField isMultiSelect={isNonBatchPropertyDisabled}>
            <div className="flex flex-col gap-2">
              <Input
                id="pdf-option-group-name"
                label={(
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
                    {isCheckboxGroupField ? 'Checkbox group name' : 'Radio group name'}
                    <ToolTip title={isCheckboxGroupField
                      ? 'This name is shared across every option in the checkbox group.'
                      : 'This name is shared across every option in the radio group.'}>
                      <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5 text-gray-500" />
                    </ToolTip>
                  </span>
                )}
                value={selectedField.groupName ?? selectedField.name}
                placeholder={isCheckboxGroupField
                  ? 'Enter checkbox group name'
                  : 'Enter radio group name'}
                disabled={!selectedField || isNonBatchPropertyDisabled}
                onChange={isCheckboxGroupField
                  ? onCheckboxGroupNameChange
                  : onRadioGroupNameChange}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
                {fieldAliasLabel}
              </div>
              <FieldAliasControl
                id="pdf-field-alias"
                placeholderClosed="e.g. Name"
                disabled={!selectedField || !canSetAlias || isNonBatchPropertyDisabled}
                showBlockedTooltip={!canSetAlias && !isNonBatchPropertyDisabled}
                selectedAlias={selectedAlias}
                aliasInputValue={aliasInputValue}
                aliasDefinitions={aliasDefinitions}
                isAliasOpen={isAliasOpen}
                onAliasChange={handleAliasChange}
                onAliasOpen={() => {
                  setIsAliasOpen(true);
                  setAliasInputValue('');
                }}
                onAliasClose={() => {
                  setIsAliasOpen(false);
                  setAliasInputValue(selectedAlias?.label ?? '');
                }}
                onAliasFocus={() => {
                  setIsAliasOpen(true);
                  setAliasInputValue('');
                }}
                onAliasInputChange={(value, reason) => {
                  if (isAliasOpen && reason === 'reset') {
                    return;
                  }
                  setAliasInputValue(value);
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Input
                id="pdf-selected-radio-name"
                label={fieldNameLabel}
                value={selectedField?.name ?? ''}
                placeholder="Enter selected field name"
                disabled={!selectedField || isNonBatchPropertyDisabled}
                onChange={onFieldNameChange}
              />
            </div>
            </MultiSelectDisabledField>
            <SignerField
              selectId="pdf-radio-field-signer"
              selectedAssigneeOption={selectedAssigneeOption}
              allowsAssigneeOverride={allowsAssigneeOverride}
              categoryRule={categoryRule}
              infoTooltip={RADIO_GROUP_ASSIGNEE_TOOLTIP}
              onFieldAssigneeChange={onFieldAssigneeChange}
            />
            <MultiSelectDisabledField isMultiSelect={isNonBatchPropertyDisabled}>
            <OptionalField
              id="pdf-option-group-field-optional"
              isRequired={isCheckboxGroupField
                ? getCheckboxGroupIsRequired(selectedOptionGroupFields)
                : getRadioGroupIsRequired(selectedOptionGroupFields)}
              disabled={!canToggleFieldOptional(selectedField?.type) || isNonBatchPropertyDisabled}
              disabledTooltip={isNonBatchPropertyDisabled
                ? MULTI_SELECT_DISABLED_TOOLTIP
                : SIGNATURE_OPTIONAL_DISABLED_TOOLTIP}
              infoTooltip={RADIO_GROUP_OPTIONAL_TOOLTIP}
              onChange={onFieldRequiredChange}
            />
            </MultiSelectDisabledField>
            {textFormatOptions}
            <MultiSelectDisabledField isMultiSelect={isNonBatchPropertyDisabled}>
            <div className="flex flex-col gap-3 border-t border-secondary/20 pt-3">
              <div className="flex flex-col gap-2">
                {selectedOptionGroupFields.map((field) => {
                  const isSelectedOption = isAwaitingOptionGroupOption
                    && field.id === selectedField?.id;
                  return (
                    <button
                      key={field.id}
                      type="button"
                      className={clsx(
                        'flex items-center gap-3 rounded px-2 py-1 text-left text-sm',
                        {
                          'bg-secondary/10': isSelectedOption,
                          'bg-transparent': !isSelectedOption,
                          'text-gray-500': isAwaitingOptionGroupOption,
                        },
                      )}
                      disabled={isAwaitingOptionGroupOption || isNonBatchPropertyDisabled}
                      onClick={() => { onSelectField(field.id); }}
                    >
                      <span className="font-semibold text-primary">Option</span>
                      <span className="text-gray-700">
                        {field.name}
                      </span>
                    </button>
                  );
                })}
              </div>
              {isAwaitingOptionGroupOption ? (
                <div className="rounded border border-secondary/15 bg-secondary/10 px-4 py-3 text-center text-sm font-semibold text-secondary">
                  Draw a field to connect!
                </div>
              ) : (
                <Button
                  variant="text"
                  className="!w-fit !justify-start !px-0 !py-0 !text-sm !font-medium !normal-case !text-secondary transition-colors duration-150 hover:!text-primary"
                  disabled={isNonBatchPropertyDisabled}
                  onClick={isCheckboxGroupField
                    ? onAddCheckboxGroupOption
                    : onAddRadioGroupOption}
                >
                  Add another option
                </Button>
              )}
            </div>
            </MultiSelectDisabledField>
          </>
        ) : (
          <>
            <MultiSelectDisabledField isMultiSelect={isNonBatchPropertyDisabled}>
            <div className="flex flex-col gap-2">
              <Input
                id="pdf-field-name"
                label={fieldNameLabel}
                value={selectedField?.name ?? ''}
                placeholder="Enter field name"
                disabled={!selectedField || isNonBatchPropertyDisabled}
                onChange={onFieldNameChange}
              />
            </div>
            <div className="flex flex-col gap-2">
              <div className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
                {fieldAliasLabel}
              </div>
              <FieldAliasControl
                id="pdf-field-alias"
                placeholderClosed="Select alias"
                disabled={!selectedField || !canSetAlias || isNonBatchPropertyDisabled}
                showBlockedTooltip={!canSetAlias && !isNonBatchPropertyDisabled}
                selectedAlias={selectedAlias}
                aliasInputValue={aliasInputValue}
                aliasDefinitions={aliasDefinitions}
                isAliasOpen={isAliasOpen}
                onAliasChange={handleAliasChange}
                onAliasOpen={() => {
                  setIsAliasOpen(true);
                  setAliasInputValue('');
                }}
                onAliasClose={() => {
                  setIsAliasOpen(false);
                  setAliasInputValue(selectedAlias?.label ?? '');
                }}
                onAliasFocus={() => {
                  setIsAliasOpen(true);
                  setAliasInputValue('');
                }}
                onAliasInputChange={(value, reason) => {
                  if (isAliasOpen && reason === 'reset') {
                    return;
                  }
                  setAliasInputValue(value);
                }}
              />
            </div>
            </MultiSelectDisabledField>
            <SignerField
              selectId="pdf-field-signer"
              selectedAssigneeOption={selectedAssigneeOption}
              allowsAssigneeOverride={allowsAssigneeOverride}
              categoryRule={categoryRule}
              onFieldAssigneeChange={onFieldAssigneeChange}
            />
            <MultiSelectDisabledField isMultiSelect={isNonBatchPropertyDisabled}>
            <OptionalField
              id="pdf-field-optional"
              isRequired={selectedField?.isRequired ?? true}
              disabled={!canToggleFieldOptional(selectedField?.type) || isNonBatchPropertyDisabled}
              disabledTooltip={isNonBatchPropertyDisabled
                ? MULTI_SELECT_DISABLED_TOOLTIP
                : SIGNATURE_OPTIONAL_DISABLED_TOOLTIP}
              onChange={onFieldRequiredChange}
            />
            </MultiSelectDisabledField>
            {textFormatOptions}
          </>
        )}
      </div>
    </div>
  );
}
